import { WORKSPACE_ROLES, type WorkspaceRole } from '@noconflict/contracts';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../shared/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../shared/ui/select';

const NONE = 'none';
/** `id` null is "All workspaces" (the org-wide grant). */
export type GrantTarget = { id: string | null; name: string; archived: boolean };

/**
 * The rows of the grants editor: one role select per target. Presentational, so the Members tab (saves each
 * change at once) and the invite dialog (collects a draft) show exactly the same control.
 */
export function GrantRoleFields({
  targets,
  roleFor,
  onChange,
  labelSuffix,
  disabled = false,
}: {
  targets: readonly GrantTarget[];
  roleFor: (targetId: string | null) => WorkspaceRole | null;
  onChange: (targetId: string | null, role: WorkspaceRole | null) => void;
  /** Completes each select's accessible name: "Role in Season One: Ada Lovelace". */
  labelSuffix?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation('member');
  return (
    <ul className="divide-y rounded-sm border">
      {targets.map((target) => {
        const label =
          target.id === null ? t('grants.roleAll') : t('grants.roleIn', { workspace: target.name });
        return (
          <li
            key={target.id ?? 'all'}
            className="flex flex-wrap items-center justify-between gap-3 px-3 py-2"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className={target.id === null ? 'font-bold' : 'truncate'}>
                {target.id === null ? t('grants.all') : target.name}
              </span>
              {target.archived ? <Badge size="compact">{t('grants.archived')}</Badge> : null}
            </span>
            <Select
              value={roleFor(target.id) ?? NONE}
              disabled={disabled}
              onValueChange={(value) =>
                onChange(target.id, value === NONE ? null : (value as WorkspaceRole))
              }
            >
              <SelectTrigger
                size="compact"
                className="w-36"
                aria-label={labelSuffix ? `${label}: ${labelSuffix}` : label}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t('roles.none')}</SelectItem>
                {WORKSPACE_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {t(`roles.${role}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </li>
        );
      })}
    </ul>
  );
}
